import React, { useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, Note, Scrollbar } from '@repo/design-system';
import { semantic, space, typeRole, weight } from '@repo/design-tokens';
import { useEquipmentCatalogStore } from '@repo/front-end-state';
import { surfaceDisplayName } from '@repo/front-end-utils';
import type { FloorPlannerItem, FloorPlannerSurface } from '@repo/frontend-types';

import type { NoteTone } from '@repo/design-system';

/**
 * Whether the list is open, for the page's life: across cameras and both steps, collapsed at first
 * (`unified-camera-panel`, R4.4). Session memory only — a module value, never browser storage.
 */
let sightListOpen = false;

/** Back to collapsed, as a fresh page starts. For tests, which share the module between cases. */
export const resetSightListMemory = () => {
  sightListOpen = false;
};

/** A list longer than this scrolls inside the panel rather than growing it (R4.5). */
const SCROLL_AFTER = 8;

/**
 * A machine's name on the list (R4.2): the name the plan shows; else the model its register row
 * names (`model_raw`, the row an item is linked to by `assetUid`); else "Unnamed machine".
 */
export const machineName = (
  item: FloorPlannerItem,
  modelOf: (assetUid: string) => string | undefined,
): string =>
  item.name?.trim() || (item.assetUid ? modelOf(item.assetUid) : undefined) || 'Unnamed machine';

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export interface CameraSightListProps {
  /** The sight sentence (`cameraSightSentence`), kept as it is. */
  sentence: string;
  /** What the cone covers, from the same sight calculation as the sentence (`getCameraSight`). */
  items: readonly FloorPlannerItem[];
  surfaces: readonly FloorPlannerSurface[];
  tone: NoteTone;
  onAnnounce: (text: string) => void;
}

/**
 * What a camera sees (`unified-camera-panel`, R4): the sentence, a Show/Hide button after it, and the
 * list it counts — Machines then Spaces, each alphabetical, an empty group not drawn. The list and
 * the sentence come from the one sight result, so they always agree, and both follow a turn, widen,
 * reach or move as it happens.
 */
const CameraSightList: React.FC<CameraSightListProps> = ({
  sentence,
  items,
  surfaces,
  tone,
  onAnnounce,
}) => {
  const [open, setOpen] = useState(sightListOpen);
  const listId = useId();
  const registerRows = useEquipmentCatalogStore(s => s.registerRows);
  const modelOf = useMemo(() => {
    const byUid = new Map<string, string>();
    registerRows.forEach(row => {
      const model = row.model_raw?.trim();
      if (model) byUid.set(String(row.id), model);
    });
    return (assetUid: string) => byUid.get(assetUid);
  }, [registerRows]);
  const machines = items.map(item => machineName(item, modelOf)).sort(byName);
  const spaces = surfaces.map(surfaceDisplayName).sort(byName);
  const total = machines.length + spaces.length;
  const shown = open && total > 0;

  const toggle = () => {
    const next = !open;
    sightListOpen = next;
    setOpen(next);
    onAnnounce(
      next
        ? `Showing ${count(machines.length, 'machine', 'machines')} and ${count(spaces.length, 'space', 'spaces')}`
        : 'List hidden',
    );
  };

  const groups = (
    <Groups>
      {(
        [
          ['Machines', machines],
          ['Spaces', spaces],
        ] as const
      ).map(([title, names]) =>
        names.length > 0 ? (
          <Group key={title}>
            <GroupTitle id={`${listId}-${title}`}>{title}</GroupTitle>
            <ul aria-labelledby={`${listId}-${title}`}>
              {names.map((name, i) => (
                <li key={`${name}-${i}`}>{name}</li>
              ))}
            </ul>
          </Group>
        ) : null,
      )}
    </Groups>
  );

  return (
    <div>
      <Line>
        <Note tone={tone} bare icon={<Icon name="eye" size="xs" />}>
          {sentence}
        </Note>
        <Button
          variant="text"
          size="xs"
          aria-label={open ? 'Hide what this camera sees' : 'Show what this camera sees'}
          aria-expanded={shown}
          aria-controls={shown ? listId : undefined}
          disabled={total === 0}
          onClick={toggle}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Line>
      {shown ? (
        <List id={listId} data-testid="camera-sight-list">
          {total > SCROLL_AFTER ? (
            // Past eight entries it scrolls inside the panel, on the design system's bar (R4.5).
            <Scrollbar maxHeight={LIST_MAX_HEIGHT} variant="glass">
              {groups}
            </Scrollbar>
          ) : (
            groups
          )}
        </List>
      ) : null}
    </div>
  );
};

export default CameraSightList;

const Line = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[2]};
`;

/* About eight entries' height, then the list scrolls inside the panel. */
const LIST_MAX_HEIGHT = 200;

const List = styled.div`
  margin-top: ${space[2]};
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
`;

const Group = styled.div`
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.body};
  }
`;

/* A label for its list, not a heading: the panel's outline stays the card's own title. */
const GroupTitle = styled.span`
  display: block;
  margin: 0 0 ${space[1]};
  font-size: ${typeRole.caption2.fontSize};
  line-height: ${typeRole.caption2.lineHeight};
  font-weight: ${weight.semibold};
  color: ${semantic.text.secondary};
`;
